/**
 * CustomCamposBusca.tsx — o campo que se DIGITA para achar, em vez de rolar.
 *
 * Pedido dele, 05/09/2026, olhando o select "Vinculada à OS":
 *
 *   "Essa caixa de seleção, por exemplo, Vinculada à OS, legal. Só que porra,
 *   tem uma porrada de OS, não dá. A gente tem que ter um campo automático aí
 *   para você começar a buscar — você começa a digitar e ela aparece."
 *
 * ─── POR QUE NÃO O `choices.js` DO TEMA ──────────────────────────────────────
 * O AdminUX traz o `choices.js`, com CSS pronto, e ele até tem busca — mas
 * **busca no que já está na página**. Para servir, o select teria de carregar
 * todas as OS antes de a pessoa digitar a primeira letra, que é exatamente o
 * problema que ele descreveu ("tem uma porrada de OS"). Aqui a busca vai ao
 * SERVIDOR, que é onde a lista está, e volta com as dez primeiras.
 *
 * O desenho, esse sim, é do app: a lista de sugestões é a mesma peça do menu de
 * menções do chat (`casaa-mensagens-sugestoes`), que ele já aprovou.
 *
 * ─── O TECLADO MANDA ─────────────────────────────────────────────────────────
 * ↑ ↓ andam, Enter escolhe, Esc fecha, Backspace num campo já escolhido volta a
 * buscar. É o mesmo acordo do @ do chat — quem digita rápido não quer o mouse.
 */
import { useEffect, useRef, useState } from 'react';

export interface ItemBusca {
  id: number;
  /** A linha principal — "OS 148". */
  rotulo: string;
  /** A linha de baixo, para desempatar dois parecidos — cliente, cidade. */
  detalhe?: string | null;
}

interface Props {
  id: string;
  label: string;
  /** O escolhido, ou `null`. O pai é o dono do valor. */
  valor: ItemBusca | null;
  onChange: (item: ItemBusca | null) => void;
  /** Vai ao servidor. Devolve poucos — dez é o que cabe sem rolar. */
  buscar: (termo: string) => Promise<ItemBusca[]>;
  /** O que se lê enquanto o campo está vazio. */
  placeholder?: string;
  /** O texto de ajuda sob o campo. */
  ajuda?: React.ReactNode;
  desabilitado?: boolean;
}

export function CustomCampoBusca({
  id, label, valor, onChange, buscar, placeholder, ajuda, desabilitado,
}: Props) {
  const [termo, setTermo] = useState('');
  const [itens, setItens] = useState<ItemBusca[]>([]);
  const [ativo, setAtivo] = useState(0);
  const [aberto, setAberto] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const caixaRef = useRef<HTMLDivElement>(null);

  /* ⏱️ 250 ms de espera antes de ir ao servidor. Sem isso, "148" são três
     buscas, e a terceira pode voltar antes da segunda — a lista pisca com o
     resultado errado. O `cancelado` descarta a resposta de uma busca que já
     não interessa. */
  useEffect(() => {
    if (!aberto) return;
    const t = (termo || '').trim();
    if (t.length < 2) { setItens([]); return; }
    let cancelado = false;
    setBuscando(true);
    const timer = setTimeout(() => {
      buscar(t)
        .then((r) => { if (!cancelado) { setItens(r); setAtivo(0); } })
        .catch(() => { if (!cancelado) setItens([]); })
        .finally(() => { if (!cancelado) setBuscando(false); });
    }, 250);
    return () => { cancelado = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termo, aberto]);

  /* Clicar fora fecha — senão a lista fica pendurada sobre o resto da tela. */
  useEffect(() => {
    if (!aberto) return;
    const fora = (ev: MouseEvent) => {
      if (caixaRef.current && !caixaRef.current.contains(ev.target as Node)) setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  function escolher(item: ItemBusca) {
    onChange(item);
    setTermo('');
    setItens([]);
    setAberto(false);
  }

  function aoTeclar(ev: React.KeyboardEvent<HTMLInputElement>) {
    if (!aberto || itens.length === 0) {
      if (ev.key === 'Escape') setAberto(false);
      return;
    }
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      setAtivo((i) => (i + 1) % itens.length);
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      setAtivo((i) => (i - 1 + itens.length) % itens.length);
    } else if (ev.key === 'Enter') {
      ev.preventDefault();
      escolher(itens[ativo] ?? itens[0]);
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      setAberto(false);
    }
  }

  /* JÁ ESCOLHIDO: o campo vira o item, com um x para desfazer. Deixar o texto
     editável depois de escolher é como se perde a escolha sem querer. */
  if (valor && !aberto) {
    return (
      <div>
        <div className="casaa-busca-escolhido form-control d-flex align-items-center gap-2">
          <span className="flex-grow-1 text-truncate">
            <span className="fw-medium">{valor.rotulo}</span>
            {valor.detalhe && <span className="text-secondary"> · {valor.detalhe}</span>}
          </span>
          {!desabilitado && (
            <button type="button" className="btn btn-link btn-sm p-0 text-secondary"
                    aria-label={`Trocar ${label}`}
                    onClick={() => { onChange(null); setTermo(''); setAberto(true); }}>
              <i className="bi bi-x-lg"></i>
            </button>
          )}
        </div>
        {ajuda && <div className="form-text">{ajuda}</div>}
      </div>
    );
  }

  return (
    <div ref={caixaRef} className="position-relative">
      <div className="form-floating">
        <input
          id={id}
          type="text"
          className="form-control"
          placeholder={placeholder ?? ' '}
          autoComplete="off"
          disabled={desabilitado}
          value={termo}
          onChange={(ev) => { setTermo(ev.target.value); setAberto(true); }}
          onFocus={() => setAberto(true)}
          onKeyDown={aoTeclar}
        />
        <label htmlFor={id}>{label}</label>
      </div>

      {aberto && (termo.trim().length >= 2 || buscando) && (
        <div className="casaa-mensagens-sugestoes casaa-busca-lista bg-body border rounded shadow-sm py-1">
          {buscando && itens.length === 0 && (
            <div className="px-3 py-2 small text-secondary">
              <span className="spinner-border spinner-border-sm me-2" />Procurando…
            </div>
          )}
          {!buscando && itens.length === 0 && (
            <div className="px-3 py-2 small text-secondary">Nada com esse termo.</div>
          )}
          {itens.map((item, i) => (
            <button
              type="button"
              key={item.id}
              onMouseEnter={() => setAtivo(i)}
              onClick={() => escolher(item)}
              className={`btn btn-link text-decoration-none text-start w-100 px-3 py-1${
                i === ativo ? ' casaa-mensagens-sugestao--ativa' : ''
              }`}
            >
              <span className="fw-medium">{item.rotulo}</span>
              {item.detalhe && <span className="text-secondary small"> · {item.detalhe}</span>}
            </button>
          ))}
        </div>
      )}

      {ajuda && <div className="form-text">{ajuda}</div>}
    </div>
  );
}
